import React, { useMemo } from 'react';
import { Menu } from 'antd';
import { Dropdown } from '@/components/AntdCompatDropdown';
import './Style.css';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ClockShareIcon,
} from '@/assets/icons/icons';
import { CURATED_TIMEZONES } from '@/common/static_data';
import { DisplayTimeSelection, Props } from './types';

function labelForSelection(sel: DisplayTimeSelection) {
  if (sel.type === 'preset') {
    switch (sel.value) {
      case 'local_scan_time':
        return 'Local scan time';
      case 'my_local_time':
        return 'My local time';
      case 'my_browser_time':
        return 'My browser time';
      case 'origin':
        return 'Origin time';
      case 'destination':
        return 'Destination time';
    }
  }
  return sel.value.split('_').join(' ');
}

const DisplayTimeDropdown: React.FC<Props> = ({
  label = 'Display time',
  value,
  onChange,
  open,
  setOpen,
  hideLocalScanTime = false,
  hideMyBrowserTime = true,
  disabledKeys = [],
}) => {
  const timezoneItems = useMemo(
    () =>
      CURATED_TIMEZONES.map((tz) => ({
        key: `tz::${tz}`,
        label: tz.split('_').join(' '),
      })),
    []
  );

  const presetItems = [
    !hideLocalScanTime && {
      key: 'local_scan_time',
      label: 'Local scan time',
      disabled: disabledKeys.includes('local_scan_time'),
    },
    {
      key: 'my_local_time',
      label: 'My local time',
      disabled: disabledKeys.includes('my_local_time'),
    },
    !hideMyBrowserTime && {
      key: 'my_browser_time',
      label: 'My browser time',
      disabled: disabledKeys.includes('my_browser_time'),
    },
    {
      key: 'origin',
      label: 'Origin time',
      disabled: disabledKeys.includes('origin'),
    },
    {
      key: 'destination',
      label: 'Destination time',
      disabled: disabledKeys.includes('destination'),
    },
  ].filter(Boolean);

  const menu = (
    <Menu
      onClick={({ key }) => {
        if (key.startsWith('tz::')) {
          onChange({ type: 'timezone', value: key.slice(4) });
        } else {
          const map = {
            local_scan_time: {
              type: 'preset',
              value: 'local_scan_time',
            } as const,
            my_local_time: { type: 'preset', value: 'my_local_time' } as const,
            my_browser_time: {
              type: 'preset',
              value: 'my_browser_time',
            } as const,
            origin: { type: 'preset', value: 'origin' } as const,
            destination: { type: 'preset', value: 'destination' } as const,
          };

          onChange(map[key as keyof typeof map]);
        }

        setOpen(false);
      }}
      items={[
        ...presetItems,
        { type: 'divider' },
        {
          type: 'group',
          label: <span className='dt-menu-group-label'>Timezones</span>,
          children: timezoneItems,
        },
      ]}
      className='dt-menu'
      rootClassName='custom-timezone-menu'
    />
  );

  return (
    <div className='dt-container'>
      <span className='dt-label'>
        <ClockShareIcon className='dt-clock-icon' />
        {label}
        {label ? ':' : null}
      </span>

      <Dropdown
        overlay={menu}
        trigger={['click']}
        open={open}
        onOpenChange={setOpen}
        placement='bottomLeft'
        getPopupContainer={(trigger) => trigger.parentElement as HTMLElement}
      >
        <span className='dt-trigger'>
          {labelForSelection(value)}

          {open ? (
            <ChevronUpIcon
              className='dt-chevron-icon'
              size={12}
              color='var(--text-secondary)'
            />
          ) : (
            <ChevronDownIcon
              className='dt-chevron-icon'
              size={12}
              color='var(--text-secondary)'
            />
          )}
        </span>
      </Dropdown>
    </div>
  );
};

export default DisplayTimeDropdown;
